import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/utils/utils";

interface BackButtonProps extends Omit<ButtonProps, "onClick"> {
  /**
   * The text label for the button
   * @default "Back"
   */
  label?: string;
  /**
   * Whether to show the arrow icon
   * @default true
   */
  showIcon?: boolean;
  /**
   * Optional custom onClick handler (will navigate back after executing)
   */
  beforeNavigate?: () => void;
}

/**
 * A reusable back button component that uses browser history to navigate back.
 * Uses React Router's navigate(-1) which is equivalent to window.history.back()
 *
 * @example
 * // Basic usage
 * <BackButton />
 *
 * @example
 * // Custom label without icon
 * <BackButton label="Return to Previous Page" showIcon={false} />
 *
 * @example
 * // With custom styling
 * <BackButton className="mb-4" variant="outline" />
 */
export function BackButton({
  label = "Back",
  showIcon = true,
  className,
  variant = "link",
  beforeNavigate,
  ...props
}: BackButtonProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (beforeNavigate) {
      beforeNavigate();
    }

    // Check if we have valid browser history to go back to
    // history.length of 1 means this is the only page in the history stack
    if (window.history.length > 1) {
      // We have history, use the browser's back functionality
      navigate(-1);
    } else {
      // No valid history (user refreshed or came directly to this page)
      // Use smart fallbacks based on current URL path
      const path = window.location.pathname;

      if (path.includes("/resources/")) {
        // On a resource detail page, go back to resource library
        navigate("/hub/resources");
      } else if (path.includes("/news/")) {
        // On a news page, go back to news listing
        navigate("/hub/news");
      } else if (path.includes("/event-registration/")) {
        // In event registration, go to events list
        navigate("/event-registration/events");
      } else if (path.includes("/lead-generation/")) {
        // In lead generation section, go to dashboard
        navigate("/lead-generation/sales-and-customer-dashboard");
      } else if (path.includes("/admin/")) {
        // In admin section, go to admin home
        navigate("/admin");
      } else if (path.includes("/hub/")) {
        // Anywhere else in hub, go to hub dashboard
        navigate("/hub");
      } else {
        // Ultimate fallback - go to root
        navigate("/");
      }
    }
  };

  return (
    <Button
      className={cn(
        variant === "link" &&
          "px-0 flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white",
        className,
      )}
      onClick={handleClick}
      variant={variant}
      {...props}
    >
      {showIcon && <ArrowLeft className="h-4 w-4" />}
      {label}
    </Button>
  );
}

export default BackButton;
